import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import {
  DropdownEntry,
  DropdownGroup,
} from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import {
  RevampTableRoot,
  TableCell,
  TableHeader,
  TableHeaderIcons,
} from '@design/components/table';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortDirection,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { BaseErrorView } from '@terminal/components/base-error-view/BaseErrorView';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { TabsType as AOTabsType } from '@terminal/modules/tab-content/types';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { getCalendarEventType } from '@terminal/views/dashboard/calendar-widget/calendar-date-card/calendar-day-card/utils';
import { CalendarEventDrawer } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/CalendarEventDrawer';
import { getHeaderProps } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/calendar-event-drawer-header/utils';
import { getDrawerContent } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/utils';
import {
  CalendarEvents,
  isCompanyEvent,
  isEarningsEvent,
} from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../empty-state/EmptyState';
import { formatEventDate, TabsType } from '../utils';
import styles from './EventsTable.module.scss';
import { EventTableBodySkeletonLoader } from './EventTableBodySkeletonLoader';

export interface TableDataSet {
  data: Record<string, CalendarEvents[]>;
  isLoading: boolean;
  isError: boolean;
  hasNextPage?: boolean;
}

export interface EventsTableProps {
  activeTab: TabsType;
  activeDate: Date;
  columns: ColumnDef<CalendarEvents>[];
  tableDataSet: TableDataSet;
  forceIsTableLoading: boolean;
  setForceIsTableLoading: (isLoading: boolean) => void;
  tableContainerRef?: React.RefObject<HTMLDivElement | null>;
  onRowSelect?: (row: Row<CalendarEvents>) => boolean;
}

export const EventsTable = ({
  activeTab,
  activeDate,
  columns,
  tableDataSet,
  forceIsTableLoading,
  setForceIsTableLoading,
  tableContainerRef,
  onRowSelect,
}: EventsTableProps) => {
  const { t } = useTranslation('common');
  const { navigateToPage } = useNavigateToAssetOverview();
  const { trackEventsDrawerOpen } = usePosthogEvents();
  const { setQueryParams, deleteQueryParam } = useQueryParams();

  const [drawerData, setDrawerData] = useState<CalendarEvents | undefined>();
  const [activeSortingState, setActiveSortingState] = useState<SortingState>(
    []
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setForceIsTableLoading(false);
    }, 20);
    return () => clearTimeout(handler);
  }, [forceIsTableLoading, setForceIsTableLoading]);

  const onFullScreenClick = (data: CalendarEvents) => {
    if (isEarningsEvent(data)) {
      return () =>
        navigateToPage({
          entityTag: data.entity_tag,
          source: POSTHOG_TRACKING_SOURCES.EVENTS,
          tab: AOTabsType.Earnings,
        });
    } else if (isCompanyEvent(data) && data.entity) {
      return () =>
        navigateToPage({
          entityTag: data.entity_tag,
          source: POSTHOG_TRACKING_SOURCES.EVENTS,
        });
    }

    return undefined;
  };

  const { data, isError, isLoading, hasNextPage } = tableDataSet;
  const activeDateData = data[formatEventDate(activeDate)] ?? [];

  const getAlignment = (index: number) => {
    switch (activeTab) {
      case TabsType.Earnings:
      case TabsType.Economics:
        return index > 2 ? 'end' : 'start';
      case TabsType.Company:
      default:
        return 'start';
    }
  };

  const getDropdownItems = (
    columnId: string,
    sortOrder: false | SortDirection
  ) => {
    const enabledKeys = sortOrder
      ? ['sortUp', 'sortDown', 'clearSort']
      : ['sortUp', 'sortDown'];

    const items: DropdownEntry[] = [
      {
        label: t('common:table.dropdown_ascending'),
        iconName: 'SortUp',
        key: 'sortUp',
        onClick: () =>
          setActiveSortingState([
            {
              id: columnId,
              desc: false,
            },
          ]),
      },
      {
        label: t('common:table.dropdown_descending'),
        iconName: 'SortDown',
        key: 'sortDown',
        onClick: () =>
          setActiveSortingState([
            {
              id: columnId,
              desc: true,
            },
          ]),
      },
      {
        label: t('common:table.dropdown_clearSort'),
        iconName: 'ClearSort',
        key: 'clearSort',
        onClick: () => setActiveSortingState([]),
      },
    ];

    return items.filter(i => enabledKeys.includes(i.key));
  };

  const onRowClick = (row: Row<CalendarEvents>) => {
    const handledByParent = onRowSelect?.(row) ?? false;

    if (!handledByParent) {
      openCalendarEventDrawer(row);
    }
  };

  const openCalendarEventDrawer = (row: Row<CalendarEvents>) => {
    setQueryParams({
      [queryKeys.activeDrawer]: `${activeTab}-events`,
    });
    const eventType = getCalendarEventType(row.original);
    setDrawerData(row.original);
    trackEventsDrawerOpen(POSTHOG_TRACKING_SOURCES.EVENTS, eventType);
  };

  const table = useReactTable<CalendarEvents>({
    data: activeDateData,
    columns,
    state: {
      sorting: activeSortingState,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isError) {
    return <BaseErrorView className={styles.styledBaseErrorView} />;
  }

  if (!activeDateData?.length && !isLoading) {
    return <EmptyState activeTab={activeTab} />;
  }

  return (
    <>
      <div
        className={styles.tableWrapper}
        ref={tableContainerRef}
        data-testid="events-table"
      >
        <RevampTableRoot>
          <thead>
            {table.getHeaderGroups().map(headerGroup => {
              return (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    const sortOrder = header.column.getIsSorted();
                    const alignment = getAlignment(index);
                    const dropdownItems = getDropdownItems(
                      header.column.id,
                      sortOrder
                    );
                    return (
                      <DropdownMenu
                        key={header.id}
                        triggerNode={
                          <TableHeader
                            className={styles.styledTableHeader}
                            key={header.column.id}
                            isHoverEnabled={false}
                            alignment={alignment}
                          >
                            <TableHeaderIcons
                              isFiltered={header.column.getIsFiltered()}
                              sortDirection={sortOrder}
                              alignment={alignment}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </TableHeaderIcons>
                          </TableHeader>
                        }
                      >
                        <DropdownGroup items={dropdownItems} />
                      </DropdownMenu>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {isLoading || forceIsTableLoading ? (
              <EventTableBodySkeletonLoader
                columnLength={table.getHeaderGroups()[0].headers.length}
              />
            ) : (
              <>
                {table.getRowModel().rows.map(row => {
                  return (
                    <tr key={row.id} onClick={() => onRowClick(row)}>
                      {row.getVisibleCells().map((cell, index) => {
                        const isEntityCell = cell.column.id === 'entity';
                        return (
                          <TableCell
                            className={styles.styledTableCell}
                            key={cell.id}
                            alignment={getAlignment(index)}
                            condensed={isEntityCell}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </tr>
                  );
                })}
                {hasNextPage && table.getRowModel().rows.length > 0 && (
                  <tr data-testid="next-page-loading-row">
                    {table
                      .getRowModel()
                      .rows[0].getVisibleCells()
                      .map(cell => (
                        <TableCell key={cell.id} isNumber={false}>
                          <SkeletonLoader
                            areas={[
                              {
                                styles: {
                                  width: '50%',
                                  height: '20px',
                                  borderRadius: '8px',
                                },
                              },
                            ]}
                          />
                        </TableCell>
                      ))}
                  </tr>
                )}
              </>
            )}
          </tbody>
        </RevampTableRoot>
      </div>
      <CalendarEventDrawer
        closeDrawer={() => {
          setDrawerData(undefined);
          deleteQueryParam(queryKeys.activeDrawer);
        }}
        isOpen={!!drawerData}
        headerContent={drawerData && getHeaderProps(drawerData)}
        drawerContent={
          drawerData &&
          getDrawerContent(
            drawerData,
            POSTHOG_TRACKING_SOURCES.CALENDAR_EVENT_DRAWER
          )
        }
        onFullScreenClick={drawerData && onFullScreenClick(drawerData)}
      />
    </>
  );
};
